import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { orderApi, cryptoPaymentApi } from '../../lib/supabase-api';
import { COIN_OPTIONS, COIN_ICONS, COIN_LIST } from '../../lib/crypto-config';
import { CryptoCoin, CryptoPaymentStatus, Order } from '../../types';
import QRCode from 'qrcode';

const SUPPORTED_COINS: CryptoCoin[] = ['BTC', 'USDT', 'LTC', 'XMR'];

function truncateHash(hash: string): string {
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

const CryptoPayment: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const rawCoin = searchParams.get('coin');
  const { isLoggedIn } = useAuth();
  const { clearCart } = useCart();
  // Guard: cart-clear must fire exactly once per paid transition. Without
  // this, the paid-state useEffect would re-clear on every re-render while
  // status is paid (3-second redirect delay creates a wide race window).
  const cartClearedRef = useRef(false);

  // Resolved later once we know the user's coin either from URL or from the
  // existing pending invoice (Continue Payment flow).
  const [coin, setCoin] = useState<CryptoCoin | null>(
    rawCoin && SUPPORTED_COINS.includes(rawCoin as CryptoCoin) ? (rawCoin as CryptoCoin) : null,
  );

  const [order, setOrder] = useState<Order | null>(null);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [amountCrypto, setAmountCrypto] = useState('');
  const [amountFiat, setAmountFiat] = useState(0);
  const [qrData, setQrData] = useState('');
  const [qrImage, setQrImage] = useState('');
  const [status, setStatus] = useState<CryptoPaymentStatus>('pending');
  const [txHash, setTxHash] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // PR 5: 'uri' added so the wallet-app deep-link copy button has its own state
  const [copied, setCopied] = useState<'address' | 'amount' | 'uri' | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showFineprint, setShowFineprint] = useState(false);
  // uiMode='picker' shows the coin-picker view without creating an invoice
  // (post-cancel, expired-retry, or entry-without-resumable-invoice). Replaces
  // the old "navigate to /contact-method" fallback that confusingly landed
  // users on the manual-payment "Order Submitted!" page mid-flow.
  const [uiMode, setUiMode] = useState<'paying' | 'picker'>('paying');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const coinInfo = coin ? COIN_OPTIONS[coin] : null;

  // Resolve coin if missing from URL: look up an existing pending invoice for
  // this order so Continue Payment from MyPage works without the user having
  // to pick again. If nothing is resumable (cancelled/expired/paid), drop
  // into the picker view so the user can pick a coin — DON'T punt them to
  // /contact-method, which is the manual-payment "Order Submitted!" landing.
  useEffect(() => {
    if (coin || !orderId || !isLoggedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await cryptoPaymentApi.getByOrderId(orderId);
        if (cancelled) return;
        if (
          existing &&
          existing.status === 'pending' &&
          SUPPORTED_COINS.includes(existing.coin as CryptoCoin)
        ) {
          setCoin(existing.coin as CryptoCoin);
        } else {
          setUiMode('picker');
          setIsLoading(false);
        }
      } catch {
        if (cancelled) return;
        setUiMode('picker');
        setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coin, orderId, isLoggedIn]);

  // Load order and create / reuse invoice. Gated on uiMode='paying' so
  // switching into picker doesn't fire a stale createInvoice, and switching
  // back out (via handlePickCoin setting coin+paying together) fires exactly
  // one fresh invoice.
  useEffect(() => {
    if (!isLoggedIn) { navigate('/login'); return; }
    if (uiMode !== 'paying') return;
    if (!orderId || !coin) return;

    const init = async () => {
      try {
        const foundOrder = await orderApi.getById(orderId);
        if (!foundOrder) { setError('Order not found'); setIsLoading(false); return; }
        setOrder(foundOrder);

        const result = await cryptoPaymentApi.createInvoice(orderId, coin);
        if (!result.ok) {
          // PR 5: route specific failures. below_min_amount → drop into the
          // coin picker so the user can pick a different coin. Other errors
          // → standard error card.
          if (result.code === 'below_min_amount') {
            const min = typeof result.minAmountUsd === 'number'
              ? `$${result.minAmountUsd.toFixed(2)}`
              : 'higher';
            setError(`${coin} requires a minimum order of ${min}. Pick another coin.`);
            setIsLoading(false);
            // Bring user back to picker rather than dead-end them
            setTimeout(() => { setError(null); setUiMode('picker'); }, 2500);
          } else {
            setError(result.message || 'Failed to create payment invoice. Please try again.');
            setIsLoading(false);
          }
          return;
        }

        setPaymentId(result.paymentId);
        setAddress(result.address);
        setAmountCrypto(result.amountCrypto);
        setAmountFiat(result.amountFiat);
        setQrData(result.qrData);
        setExpiresAt(result.expiresAt);
        setStatus(result.status || 'pending');
      } catch (e) {
        setError('Something went wrong. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [isLoggedIn, navigate, orderId, coin, uiMode]);

  // Generate QR code as SVG instead of canvas-PNG. Firefox's
  // anti-fingerprinting blocks canvas.toDataURL() ("Blocked ... from
  // extracting canvas data"), which renders the QR as solid stripes.
  // SVG output is markup-based — no canvas read — so it works in every
  // browser including hardened Firefox / Tor Browser.
  useEffect(() => {
    if (!qrData) return;
    QRCode.toString(qrData, {
      type: 'svg',
      width: 240,
      margin: 2,
      color: { dark: '#1a2e1a', light: '#faf8f5' },
    })
      .then(svg => setQrImage(`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`))
      .catch(() => {});
  }, [qrData]);

  // Countdown timer
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setTimeLeft(remaining);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  // Auto-expire — only when expiration time has actually passed (not just timeLeft === 0 from initial state)
  useEffect(() => {
    if (timeLeft === 0 && expiresAt && status === 'pending' && new Date(expiresAt) < new Date()) {
      setStatus('expired');
    }
  }, [timeLeft, expiresAt, status]);

  // Redirect on paid/confirmed (handles both initial load and poll-detected).
  // Also clears cart here — server-side webhook + check-status do the same
  // cascade for users who close the tab, but the frontend clear is what
  // visually empties the cart icon before the redirect lands.
  useEffect(() => {
    if ((status === 'paid' || status === 'confirmed') && orderId && !isLoading) {
      if (!cartClearedRef.current) {
        cartClearedRef.current = true;
        clearCart();
      }
      const timer = setTimeout(() => navigate(`/order-complete/${orderId}`, { replace: true }), 3000);
      return () => clearTimeout(timer);
    }
  }, [status, orderId, isLoading, navigate, clearCart]);

  // Poll payment status
  const pollStatus = useCallback(async () => {
    if (!paymentId) return;
    const result = await cryptoPaymentApi.checkStatus(paymentId);
    if (!result) return;
    setStatus(result.status as CryptoPaymentStatus);
    if (result.txHash) setTxHash(result.txHash);
    if (result.status === 'paid' || result.status === 'confirmed') {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    if (result.status === 'expired') {
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [paymentId]);

  useEffect(() => {
    if (!paymentId || ['paid', 'confirmed', 'expired', 'invalid'].includes(status)) return;
    pollRef.current = setInterval(pollStatus, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [paymentId, status, pollStatus]);

  // Copy to clipboard
  const handleCopy = async (text: string, type: 'address' | 'amount' | 'uri') => {
    await navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  // Reset paying-state fields so a fresh invoice render isn't contaminated
  // by the previous coin's QR/address/timer.
  const resetPayingState = () => {
    setPaymentId(null);
    setAddress('');
    setAmountCrypto('');
    setAmountFiat(0);
    setQrData('');
    setQrImage('');
    setStatus('pending');
    setTxHash(null);
    setExpiresAt('');
    setTimeLeft(0);
    setError(null);
  };

  // User picked a coin from the picker. Setting (coin, uiMode) together
  // triggers the init effect exactly once with the new coin.
  const handlePickCoin = (newCoin: CryptoCoin) => {
    resetPayingState();
    setIsLoading(true);
    setCoin(newCoin);
    setUiMode('paying');
  };

  // Cancel current invoice, then drop into the picker so the user can choose
  // a different coin (or fall through to /contact-method for manual pay).
  // Do NOT navigate away — that's what originally landed users on the
  // misleading "Order Submitted!" page.
  const handleCancel = async () => {
    if (!paymentId) return;
    setCancelling(true);
    try {
      const result = await cryptoPaymentApi.cancel(paymentId);
      if (result?.success) {
        setShowCancelConfirm(false);
        resetPayingState();
        setUiMode('picker');
      } else {
        setError(result?.error || 'Failed to cancel invoice. Please try again.');
        setShowCancelConfirm(false);
      }
    } catch (e) {
      setError((e as Error).message || 'Failed to cancel invoice. Please try again.');
      setShowCancelConfirm(false);
    } finally {
      setCancelling(false);
    }
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  // Invoice expires_at = 180min (3hr) in create-crypto-invoice; mirror the
  // denominator here so the progress bar starts at 100%. Threshold for the
  // red urgency state is timeLeft < 300s (5min) — that hasn't moved.
  const progressPercent = expiresAt
    ? Math.max(0, Math.min(100, (timeLeft / 10800) * 100))
    : 0;

  // --- Picker view — lets user pick a coin without being dumped on the
  // manual-payment /contact-method page. Entered after cancel, after expired,
  // or when there's no resumable invoice for the order.
  if (uiMode === 'picker') {
    return (
      <div className="min-h-screen bg-cream-100">
        <div className="section-container py-10 sm:py-16 max-w-lg mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-display text-2xl sm:text-3xl text-forest-900 tracking-tight">
              Choose payment method
            </h1>
            <p className="text-warm-500 text-sm mt-2">
              Pick a cryptocurrency to pay with, or switch to manual payment.
            </p>
          </div>

          <div className="bg-cream-50 rounded-2xl border border-warm-200 shadow-card p-6">
            <p className="text-[13px] font-medium text-warm-600 mb-3">Cryptocurrency</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {COIN_LIST.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handlePickCoin(c.id)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-warm-200 hover:border-forest-400 hover:bg-forest-50 transition-all text-left cursor-pointer"
                >
                  <span className={`text-xl font-bold ${c.color}`}>{COIN_ICONS[c.id]}</span>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-forest-900 truncate">{c.name}</div>
                    <div className="text-[11px] text-warm-400">{c.network}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    );
  }

  // --- Loading state (also covers "resolving coin from existing invoice") ---
  if (isLoading || !coin) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-warm-200 border-t-forest-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-warm-500 text-sm">Creating payment invoice...</p>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4">
        <div className="bg-cream-50 rounded-2xl border border-warm-200 shadow-card p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h2 className="font-display text-xl text-forest-900 mb-2">Payment Error</h2>
          <p className="text-warm-500 text-sm mb-6">{error}</p>
          <button onClick={() => navigate(-1)} className="btn-primary">Go Back</button>
        </div>
      </div>
    );
  }

  // --- Success state ---
  if (status === 'paid' || status === 'confirmed') {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4">
        <div className="bg-cream-50 rounded-2xl border border-forest-200 shadow-card p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-forest-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-forest-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="font-display text-2xl text-forest-900 mb-2">Payment Confirmed!</h2>
          <p className="text-warm-500 text-sm mb-2">Your {coinInfo?.name || coin} payment has been received.</p>
          {txHash && coinInfo && (
            <a
              href={coinInfo.explorerTxUrl(txHash)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-forest-600 hover:text-forest-800 font-medium mt-1"
            >
              Tx {truncateHash(txHash)}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
          )}
          <p className="text-warm-400 text-xs mt-2">Redirecting to order confirmation...</p>
          <div className="mt-4">
            <div className="w-8 h-8 border-3 border-warm-200 border-t-forest-600 rounded-full animate-spin mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  // --- Expired / invalid state ---
  if (status === 'expired' || status === 'invalid') {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4">
        <div className="bg-cream-50 rounded-2xl border border-warm-200 shadow-card p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="font-display text-xl text-forest-900 mb-2">
            {status === 'invalid' ? 'Invoice Cancelled' : 'Invoice Expired'}
          </h2>
          <p className="text-warm-500 text-sm mb-2">
            {status === 'invalid'
              ? 'This payment invoice was cancelled.'
              : 'The payment window has closed.'}
          </p>
          <p className="text-warm-500 text-xs mb-6">
            If you already sent crypto, it will still be credited once the network confirms — NOWPayments accepts late payments for up to 7 days. No action needed on your end.
          </p>
          <div className="flex flex-col gap-3">
            <button onClick={() => { resetPayingState(); setUiMode('picker'); }} className="w-full bg-forest-800 text-cream-100 py-3 rounded-lg font-medium hover:bg-forest-700 transition-all">
              Choose payment method
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isUsdt = coin === 'USDT';
  const showUrgentBanner = timeLeft > 0 && timeLeft < 60 && status === 'pending';

  // --- Main payment state ---
  return (
    <div className="min-h-screen bg-cream-100">
      <div className="section-container py-10 sm:py-16 max-w-lg mx-auto">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-50 border border-amber-200 rounded-full mb-4">
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
            <span className="text-[12px] font-semibold text-amber-700">Awaiting Payment</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl text-forest-900 tracking-tight">
            Send {coinInfo?.name || coin}
          </h1>
          {order && (
            <p className="text-warm-500 text-sm mt-2">Order #{order.orderNumber}</p>
          )}
        </div>

        {/* Near-expiry urgent banner */}
        {showUrgentBanner && (
          <div className="flex items-start gap-2.5 p-3 mb-4 bg-red-50 rounded-xl border border-red-300 animate-pulse">
            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <p className="text-xs text-red-700 font-medium leading-relaxed">
              Invoice expires in less than 1 minute. Complete your payment now, or the invoice will expire.
            </p>
          </div>
        )}

        {/* USDT TRC20 critical warning — prevents permanent fund loss */}
        {isUsdt && (
          <div className="flex items-start gap-2.5 p-3 mb-4 bg-red-50 rounded-xl border border-red-300">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <div className="text-xs leading-relaxed">
              <p className="text-red-700 font-bold mb-1">Send USDT on TRC20 (Tron) network ONLY</p>
              <p className="text-red-700">
                Sending on ERC20 / BSC / Polygon / Solana will result in <span className="font-bold">permanent loss of funds</span>. This cannot be recovered.
              </p>
            </div>
          </div>
        )}

        {/* Payment Card */}
        <div className="bg-cream-50 rounded-2xl border border-warm-200 shadow-card overflow-hidden">

          {/* Coin badge */}
          <div className="px-6 py-4 bg-gradient-to-r from-forest-800 to-forest-900 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{COIN_ICONS[coin]}</span>
              <div>
                <span className="text-cream-100 font-semibold text-[14px]">{coinInfo?.name}</span>
                <span className="text-cream-100/50 text-[12px] ml-2">{coinInfo?.network}</span>
              </div>
            </div>
            <span className="text-cream-100/60 text-[11px] font-medium uppercase tracking-wider">
              NOWPayments
            </span>
          </div>

          <div className="p-6 space-y-6">

            {/* QR Code */}
            <div className="flex justify-center">
              {qrImage ? (
                <div className="p-3 bg-cream-100 rounded-xl border border-warm-200">
                  <img src={qrImage} alt="Payment QR" className="w-[200px] h-[200px]" />
                </div>
              ) : (
                <div className="w-[224px] h-[224px] bg-cream-100 rounded-xl border border-warm-200 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-warm-300 border-t-forest-600 rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="bg-cream-100 rounded-xl p-4 border border-warm-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-warm-400 uppercase tracking-wider">Send exactly</span>
                <button
                  onClick={() => handleCopy(amountCrypto, 'amount')}
                  className="text-[11px] text-forest-600 hover:text-forest-800 font-medium flex items-center gap-1 transition-colors cursor-pointer"
                >
                  {copied === 'amount' ? (
                    <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg> Copied</>
                  ) : (
                    <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" /></svg> Copy</>
                  )}
                </button>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-2xl text-forest-900 tracking-tight font-bold">{amountCrypto}</span>
                <span className="text-warm-500 text-sm font-medium">{coinInfo?.symbol}</span>
              </div>
              <p className="text-warm-400 text-[11px] mt-1">≈ ${amountFiat.toLocaleString()} USD</p>
              <p className="text-warm-400 text-[11px] mt-1">+ small network fee from your wallet (not included above)</p>
              <button
                type="button"
                onClick={() => setShowFineprint((v) => !v)}
                className="text-[11px] text-forest-600 hover:text-forest-800 font-medium mt-2 flex items-center gap-1"
              >
                {showFineprint ? 'Hide details' : 'What if I send the wrong amount?'}
                <svg className={`w-3 h-3 transition-transform ${showFineprint ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
              {showFineprint && (
                <div className="mt-2 text-[11px] text-warm-500 leading-relaxed space-y-1">
                  <p>• Sending less → partial payment. Contact support to complete the order.</p>
                  <p>• Sending more → NOWPayments auto-refunds the excess to your sending address.</p>
                </div>
              )}
            </div>

            {/* Address — PR 5: split into Raw (CEX-friendly) + URI (wallet apps) */}
            <div className="bg-cream-100 rounded-xl p-4 border border-warm-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-warm-400 uppercase tracking-wider">To address</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleCopy(address, 'address')}
                    className="text-[11px] text-forest-600 hover:text-forest-800 font-medium flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    {copied === 'address' ? (
                      <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg> Copied</>
                    ) : (
                      <>Copy address</>
                    )}
                  </button>
                  {qrData !== address && (
                    <button
                      onClick={() => handleCopy(qrData, 'uri')}
                      className="text-[11px] text-forest-600 hover:text-forest-800 font-medium flex items-center gap-1 transition-colors cursor-pointer"
                      title="BIP21 URI with amount embedded — for wallet apps that auto-fill"
                    >
                      {copied === 'uri' ? (
                        <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg> Copied</>
                      ) : (
                        <>Copy URI</>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <p className="font-mono text-[13px] text-forest-900 break-all leading-relaxed">{address}</p>
              <p className="text-[11px] text-warm-500 mt-2 leading-relaxed">
                <span className="font-semibold text-warm-600">Double-check the address before sending.</span> Crypto transactions are irreversible — the wrong address means lost funds.
              </p>
            </div>

            {/* Coin-specific wallet caveat (e.g. XMR exchange withdrawal blocks) */}
            {coinInfo?.walletWarning && (
              <div className="flex items-start gap-2.5 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                <p className="text-xs text-amber-700 leading-relaxed">{coinInfo.walletWarning}</p>
              </div>
            )}

            {/* Timer */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] text-warm-400 font-medium">Time remaining</span>
                <span className={`font-mono text-[14px] font-bold ${timeLeft < 300 ? 'text-red-500' : 'text-forest-800'}`}>
                  {formatTime(timeLeft)}
                </span>
              </div>
              <div className="w-full h-1.5 bg-warm-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ${timeLeft < 300 ? 'bg-red-400' : 'bg-forest-600'}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Status + confirmation time expectation + tx hash link */}
            <div className="bg-cream-100 rounded-lg border border-warm-200 p-3 space-y-2">
              <div className="flex items-center justify-center gap-2">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-forest-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-1.5 h-1.5 bg-forest-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-1.5 h-1.5 bg-forest-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-[12px] text-warm-600 font-medium">
                  {status === 'partially_paid' ? 'Payment detected, waiting for confirmation...' : 'Waiting for payment...'}
                </span>
              </div>
              {coinInfo && (
                <p className="text-[11px] text-warm-400 text-center">
                  Typical confirmation time: {coinInfo.confirmationTimeMin.min}–{coinInfo.confirmationTimeMin.max} minutes
                </p>
              )}
              {txHash && coinInfo && (
                <div className="text-center">
                  <a
                    href={coinInfo.explorerTxUrl(txHash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-forest-600 hover:text-forest-800 font-medium"
                  >
                    Detected tx: {truncateHash(txHash)}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* "Safe to close" reassurance */}
        <div className="flex items-start gap-2.5 p-3 mt-4 bg-amber-50 rounded-xl border border-amber-200">
          <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Safe to close this window.</span> Payment is detected automatically via blockchain — you'll get an email once confirmed. Check status anytime from <span className="font-semibold">My Orders</span>.
          </p>
        </div>

        {/* Cancel + support */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            onClick={() => setShowCancelConfirm(true)}
            disabled={cancelling}
            className="text-[12px] text-warm-500 hover:text-red-600 font-medium transition-colors disabled:opacity-50"
          >
            Cancel & choose different payment
          </button>
          <a
            href="https://t.me/liberacure"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[12px] text-warm-500 hover:text-forest-600 font-medium transition-colors"
          >
            Need help? Contact support →
          </a>
        </div>

        {/* Cancel confirmation modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-forest-900/40 flex items-center justify-center z-50 px-4" onClick={() => !cancelling && setShowCancelConfirm(false)}>
            <div className="bg-cream-50 rounded-2xl border border-warm-200 shadow-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display text-lg text-forest-900 mb-3">Cancel this invoice?</h3>
              <div className="flex items-start gap-2.5 p-3 mb-4 bg-red-50 rounded-xl border border-red-200">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <p className="text-xs text-red-700 font-medium leading-relaxed">
                  Only cancel if you have <span className="font-bold">not</span> sent crypto yet. If you already sent, do NOT cancel — wait for confirmation instead.
                </p>
              </div>
              <p className="text-sm text-warm-600 mb-5">You'll be returned to the payment method selection screen.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={cancelling}
                  className="flex-1 py-2.5 border border-warm-300 text-warm-600 rounded-lg text-sm font-medium hover:bg-cream-100 transition-all disabled:opacity-50"
                >
                  Keep invoice
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="flex-1 py-2.5 bg-red-600 text-cream-100 rounded-lg text-sm font-medium hover:bg-red-700 transition-all disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling...' : 'Yes, cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptoPayment;
